// supabaseClient.js
// Initializes a Supabase client for the frontend.
// Requires environment variables (set in .env):
//   REACT_APP_SUPABASE_URL
//   REACT_APP_SUPABASE_ANON_KEY
// Falls back to (in order) SUPABASE_URL / SUPABASE_ANON_KEY (shared fullstack), then VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY.
// This lets us reuse existing env config without duplication. Service ROLE keys must NEVER be exposed here.

import { createClient } from '@supabase/supabase-js';

// Resolve URL / anon key from multiple conventional names (first non-empty wins)
const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.SUPABASE_URL ||
  process.env.VITE_SUPABASE_URL;

const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.SUPABASE_ANON_KEY ||
  process.env.VITE_SUPABASE_ANON_KEY;

// Security guard: detect accidental service role key exposure (dev only)
if (supabaseAnonKey && typeof atob === 'function') {
  try {
    const parts = supabaseAnonKey.split('.');
    if (parts.length === 3) {
      const payloadRaw = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadRaw);
      if (payload?.role === 'service_role') {
        // eslint-disable-next-line no-console
        console.error('[SECURITY] Service role key detected in frontend! Remove immediately.');
      }
    }
  } catch {}
}

export const SUPABASE_ENABLED = Boolean(supabaseUrl && supabaseAnonKey);

let supabase;
if (SUPABASE_ENABLED) {
  supabase = createClient(supabaseUrl, supabaseAnonKey, { auth: { persistSession: true } });
} else {
  // Provide a stub that throws on use to fail gracefully where not guarded.
  // eslint-disable-next-line no-console
  console.warn('[supabaseClient] Supabase disabled (missing env). Checked REACT_APP_*, SUPABASE_*, VITE_* variants. Using stub client.');
  const stub = () => { throw new Error('Supabase disabled: missing REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY'); };
  supabase = {
    from: stub,
    storage: { from: stub },
    functions: { invoke: async () => ({ error: new Error('Supabase disabled') }) },
  };
}

export { supabase };
export default supabase;
